package com.schoolconnect.app.feature.homework

import com.schoolconnect.app.datasource.network.response.HomeworkResponse
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.request.HomeworkCreateRequest
import com.schoolconnect.app.datasource.network.request.HomeworkUpdateRequest
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.designsystem.appDateInputComparable
import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.homework.HomeworkListRepository
import com.schoolconnect.app.repository.homework.HomeworkRepository
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.subject.SubjectRepository
import com.schoolconnect.app.repository.schoolstaff.SchoolStaffRepository
import com.schoolconnect.app.runtime.AppErrorCenter
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class HomeworkViewModel(
    private val schoolId: String = UserSessionStore.getHomeRoleSchoolId(),
    private val currentUserId: String = UserSessionStore.getUserId()
) {
    private val isTeacher = UserSessionStore.getHomeRoleCode() == SchoolKeys.V_ROLE_TEACHER
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var lookupJob: Job? = null
    private var saveJob: Job? = null
    private var attachmentMetadataJob: Job? = null
    private var viewJob: Job? = null
    private var actionJob: Job? = null
    private val _uiState = MutableStateFlow(HomeworkUiState())
    val uiState: StateFlow<HomeworkUiState> = _uiState.asStateFlow()

    init {
        loadLookups()
        loadPage()
    }

    fun onEvent(event: HomeworkUiEvent) {
        when (event) {
            HomeworkUiEvent.BackClicked -> Unit
            HomeworkUiEvent.AddRequested -> _uiState.update { it.copy(saveErrorMessage = "") }
            HomeworkUiEvent.RefreshClicked -> loadPage(forceRemote = true)
            is HomeworkUiEvent.SearchChanged -> {
                _uiState.update {
                    it.copy(
                        searchQuery = event.query,
                        pageIndex = 0,
                        totalCount = 0,
                        isLoading = true,
                        errorMessage = ""
                    )
                }
                loadPage(debounce = true)
            }
            is HomeworkUiEvent.FiltersChanged -> {
                _uiState.update { it.copy(filters = event.filters, pageIndex = 0, totalCount = 0, isLoading = true, errorMessage = "") }
                loadPage()
            }
            HomeworkUiEvent.FiltersReset -> {
                _uiState.update { it.copy(filters = HomeworkFilters(), searchQuery = "", pageIndex = 0, totalCount = 0, isLoading = true, errorMessage = "") }
                loadPage()
            }
            is HomeworkUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }
                loadPage()
            }
            is HomeworkUiEvent.PageSizeChanged -> {
                _uiState.update {
                    it.copy(
                        pageSize = event.pageSize.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()),
                        pageIndex = 0
                    )
                }
                loadPage()
            }
            is HomeworkUiEvent.SortChanged -> {
                _uiState.update {
                    val ascending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0, isLoading = true, errorMessage = "")
                }
                loadPage()
            }
            is HomeworkUiEvent.ViewRequested -> loadHomeworkDetails(event.homework)
            HomeworkUiEvent.ViewDismissed -> {
                viewJob?.cancel()
                _uiState.update {
                    it.copy(
                        viewingHomework = null,
                        isViewingHomeworkLoading = false,
                        viewingHomeworkError = "",
                        attachmentMetadata = emptyMap()
                    )
                }
            }
            is HomeworkUiEvent.EditRequested -> loadHomeworkDetails(event.homework, forEdit = true)
            HomeworkUiEvent.EditDismissed -> {
                viewJob?.cancel()
                _uiState.update {
                    it.copy(
                        editingHomework = null,
                        isEditingHomeworkLoading = false,
                        editingHomeworkError = "",
                        saveErrorMessage = "",
                        attachmentMetadata = emptyMap()
                    )
                }
            }
            is HomeworkUiEvent.AttachmentMetadataRequested -> loadAttachmentMetadata(event.homework)
            is HomeworkUiEvent.SaveRequested -> saveHomework(event.draft)
            is HomeworkUiEvent.DeleteRequested -> _uiState.update {
                it.copy(pendingDelete = event.homework)
            }
            HomeworkUiEvent.DeleteDismissed -> _uiState.update { it.copy(pendingDelete = null) }
            HomeworkUiEvent.DeleteConfirmed -> deleteHomework()
            is HomeworkUiEvent.CloseRequested -> _uiState.update {
                it.copy(pendingClose = event.homework)
            }
            HomeworkUiEvent.CloseDismissed -> _uiState.update { it.copy(pendingClose = null) }
            HomeworkUiEvent.CloseConfirmed -> closeHomework()
            is HomeworkUiEvent.ReminderRequested -> sendReminder(event.homework)
        }
    }

    private fun closeHomework() {
        val row = _uiState.value.pendingClose ?: return
        if (_uiState.value.isClosing) return
        actionJob?.cancel()
        actionJob = scope.launch {
            _uiState.update { it.copy(isClosing = true) }
            HomeworkRepository.close(row.backendId.ifBlank { row.homeworkId }).fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            pendingClose = null,
                            isClosing = false
                        )
                    }
                    AppToastCenter.showSuccess("Homework closed.")
                    loadPage(forceRemote = true)
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(isClosing = false)
                    }
                    AppErrorCenter.emit(throwable, source = "HomeworkViewModel", fallbackMessage = "Unable to close homework.")
                }
            )
        }
    }

    private fun deleteHomework() {
        val row = _uiState.value.pendingDelete ?: return
        if (_uiState.value.isDeleting) return
        actionJob?.cancel()
        actionJob = scope.launch {
            _uiState.update { it.copy(isDeleting = true) }
            HomeworkRepository.delete(row.backendId.ifBlank { row.homeworkId }).fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            pendingDelete = null,
                            isDeleting = false
                        )
                    }
                    AppToastCenter.showSuccess("Homework deleted.")
                    loadPage(forceRemote = true)
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(isDeleting = false)
                    }
                    AppErrorCenter.emit(throwable, source = "HomeworkViewModel", fallbackMessage = "Unable to delete homework.")
                }
            )
        }
    }

    private fun sendReminder(row: HomeworkRowUi) {
        actionJob?.cancel()
        actionJob = scope.launch {
            HomeworkRepository.sendReminder(row.backendId.ifBlank { row.homeworkId }).fold(
                onSuccess = { AppToastCenter.showSuccess("Reminder sent to the homework recipients.") },
                onFailure = { throwable ->
                    AppErrorCenter.emit(throwable, source = "HomeworkViewModel", fallbackMessage = "Unable to send the homework reminder.")
                }
            )
        }
    }

    private fun loadHomeworkDetails(row: HomeworkRowUi, forEdit: Boolean = false) {
        viewJob?.cancel()
        _uiState.update {
            it.copy(
                viewingHomework = if (forEdit) it.viewingHomework else row,
                isViewingHomeworkLoading = if (forEdit) it.isViewingHomeworkLoading else true,
                viewingHomeworkError = if (forEdit) it.viewingHomeworkError else "",
                editingHomework = if (forEdit) row else it.editingHomework,
                isEditingHomeworkLoading = forEdit,
                editingHomeworkError = "",
                saveErrorMessage = if (forEdit) "" else it.saveErrorMessage,
                attachmentMetadata = emptyMap()
            )
        }
        viewJob = scope.launch {
            val homeworkId = row.backendId.trim().ifBlank { row.homeworkId.trim() }
            HomeworkRepository.get(homeworkId).fold(
                onSuccess = { response ->
                    val detail = response.toUi(_uiState.value)
                    val resolvedDetail = detail.copy(
                        className = detail.className.takeUnless { it == "—" }.orEmpty().ifBlank { row.className },
                        subject = detail.subject.takeUnless { it == "—" }.orEmpty().ifBlank { row.subject },
                        assignedBy = detail.assignedBy.takeUnless { it == "—" }.orEmpty().ifBlank { row.assignedBy },
                        assignedDate = detail.assignedDate.takeUnless { it == "—" }.orEmpty().ifBlank { row.assignedDate },
                        dueDate = detail.dueDate.takeUnless { it == "—" }.orEmpty().ifBlank { row.dueDate },
                        status = detail.status.takeUnless { it == "—" }.orEmpty().ifBlank { row.status },
                        description = detail.description.ifBlank { row.description },
                        notifyParents = detail.notifyParents ?: row.notifyParents
                    )
                    _uiState.update {
                        it.copy(
                            viewingHomework = if (forEdit) it.viewingHomework else resolvedDetail,
                            isViewingHomeworkLoading = if (forEdit) it.isViewingHomeworkLoading else false,
                            viewingHomeworkError = if (forEdit) it.viewingHomeworkError else "",
                            editingHomework = if (forEdit) resolvedDetail else it.editingHomework,
                            isEditingHomeworkLoading = false,
                            editingHomeworkError = ""
                        )
                    }
                    loadAttachmentMetadata(detail)
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] HomeworkViewModel view details failure homeworkId=$homeworkId", throwable)
                    _uiState.update {
                        it.copy(
                            isViewingHomeworkLoading = if (forEdit) it.isViewingHomeworkLoading else false,
                            viewingHomeworkError = if (forEdit) it.viewingHomeworkError else throwable.message.orEmpty().ifBlank { "Unable to load homework details." },
                            isEditingHomeworkLoading = false,
                            editingHomeworkError = if (forEdit) throwable.message.orEmpty().ifBlank { "Unable to load homework details." } else it.editingHomeworkError
                        )
                    }
                }
            )
        }
    }

    private fun loadAttachmentMetadata(homework: HomeworkRowUi) {
        val fileIds = homework.attachmentFileIds.map { it.trim() }.filter { it.isNotBlank() }
        if (fileIds.isEmpty()) return
        attachmentMetadataJob?.cancel()
        attachmentMetadataJob = scope.launch {
            fileIds.forEachIndexed { index, fileId ->
                val metadata = FileUploadRepository.getMetadata(fileId).getOrNull() ?: return@forEachIndexed
                val mimeType = metadata.detectedMimeType.orEmpty().ifBlank { metadata.declaredMimeType.orEmpty() }
                val url = homework.attachmentFileUrls.getOrNull(index).orEmpty()
                val isImage = mimeType.startsWith("image/", ignoreCase = true) ||
                    metadata.originalFilename.orEmpty().isImageFileName()
                val previewBytes = if (isImage && url.isNotBlank()) FileUploadRepository.downloadBytes(url).getOrNull() else null
                _uiState.update { state ->
                    state.copy(
                        attachmentMetadata = state.attachmentMetadata + (fileId to HomeworkAttachmentMetadataUi(
                            fileId = fileId,
                            name = metadata.originalFilename.orEmpty().ifBlank { "Attachment ${index + 1}" },
                            type = mimeType.toAttachmentType(metadata.originalFilename),
                            sizeLabel = formatHomeworkFileSize(metadata.sizeBytes),
                            isImage = isImage,
                            previewBytes = previewBytes
                        ))
                    )
                }
            }
        }
    }

    private fun saveHomework(draft: HomeworkDraft) {
        if (_uiState.value.isSaving) return
        val normalizedSchoolId = schoolId.trim()
        val normalizedTitle = draft.title.trim()
        val normalizedClassId = draft.classSectionId.trim()
        val normalizedSubjectId = draft.subjectId.trim()
        val dueDate = parseDisplayDate(draft.dueDate)
        val todayComparable = appDateInputComparable(currentAppDateLabel())
        val dueDateComparable = appDateInputComparable(draft.dueDate)
        val validationError = when {
            normalizedSchoolId.isBlank() -> "School context is required before creating homework."
            normalizedClassId.isBlank() -> "Select a class before saving homework."
            normalizedSubjectId.isBlank() -> "Select a subject before saving homework."
            normalizedTitle.isBlank() -> "Enter a homework title."
            dueDate == null -> "Enter a valid due date in DD MMM YYYY format."
            todayComparable != null && dueDateComparable != null && dueDateComparable <= todayComparable ->
                "Due date must be after today."
            else -> null
        }
        if (validationError != null) {
            _uiState.update { it.copy(saveErrorMessage = validationError) }
            return
        }

        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            val updateId = draft.homeworkId?.trim()?.takeIf { it.isNotBlank() }
            val result = if (updateId != null) {
                HomeworkRepository.update(
                    homeworkId = updateId,
                    request = HomeworkUpdateRequest(
                        classSectionId = normalizedClassId,
                        subjectId = normalizedSubjectId,
                        title = normalizedTitle,
                        description = draft.description.trim(),
                        dueDate = "${dueDate}T00:00:00Z",
                        attachmentFileIds = draft.attachmentFileIds
                    )
                )
            } else {
                HomeworkRepository.create(
                    request = HomeworkCreateRequest(
                        classSectionId = normalizedClassId,
                        subjectId = normalizedSubjectId,
                        title = normalizedTitle,
                        description = draft.description.trim().takeIf { it.isNotBlank() },
                        dueDate = "${dueDate}T00:00:00Z",
                        assignedByTeacherId = draft.assignedByTeacherId,
                        attachmentFileIds = draft.attachmentFileIds
                    ),
                    currentUserId = currentUserId
                )
            }
            result.fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = "", saveGeneration = it.saveGeneration + 1) }
                    loadPage(forceRemote = true)
                },
                onFailure = { throwable ->
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to save homework.") }
                }
            )
        }
    }

    private fun loadLookups() {
        lookupJob?.cancel()
        lookupJob = scope.launch {
            val teacherLookup = async {
                if (isTeacher) emptyMap() else SchoolStaffRepository
                    .loadSchoolTeachers(schoolId)
                    .getOrElse { throwable ->
                        scLog("[DATA_RETRIEVE_SC] HomeworkViewModel teacher lookup failed school=$schoolId", throwable)
                        emptyList()
                    }
                    .mapNotNull { staff ->
                        val id = staff.id?.trim().orEmpty()
                        val name = staff.name?.trim().orEmpty()
                        if (id.isBlank() || name.isBlank()) null else name to id
                    }.toMap()
            }
            val teacherAssignmentsLookup = async {
                if (isTeacher) {
                    SchoolStaffRepository.loadCurrentTeacherWithAssignments().getOrElse { throwable ->
                        scLog("[DATA_RETRIEVE_SC] HomeworkViewModel current teacher assignments lookup failed school=$schoolId", throwable)
                        null
                    }
                } else null
            }
            val schoolTeacherAssignmentsLookup = async {
                if (isTeacher) emptyList() else SchoolStaffRepository.loadTeacherAssignments(schoolId)
                    .getOrElse { throwable ->
                        scLog("[DATA_RETRIEVE_SC] HomeworkViewModel teacher assignments lookup failed school=$schoolId", throwable)
                        emptyList()
                    }
            }
            val teacherIds = teacherLookup.await()
            val teacherNameById = teacherIds.entries.associate { (name, id) -> id to name }
            val teacherOptionIdsByClassSubject = buildMap<String, Map<String, String>> {
                schoolTeacherAssignmentsLookup.await().forEach { staff ->
                    val staffId = staff.id?.trim().orEmpty()
                    val teacherName = teacherNameById[staffId].orEmpty()
                    if (teacherName.isBlank()) return@forEach
                    staff.teachingAssignments.orEmpty().filter { it.isActive != false }.forEach { assignment ->
                        val classId = assignment.classSectionId?.trim().orEmpty()
                        val subjectId = assignment.subjectId?.trim().orEmpty()
                        if (classId.isBlank() || subjectId.isBlank()) return@forEach
                        val key = "$classId::$subjectId"
                        val current = get(key).orEmpty()
                        put(key, current + (teacherName to staffId))
                    }
                }
            }
            _uiState.update {
                it.copy(
                    teacherOptionIds = teacherIds,
                    teacherOptionIdsByClassSubject = teacherOptionIdsByClassSubject,
                    requiresTeacherSelection = !isTeacher
                )
            }
            val classIds = if (isTeacher) {
                teacherAssignmentsLookup.await()?.teachingAssignments.orEmpty()
                    .filter { it.isActive != false }
                    .mapNotNull { assignment ->
                    val id = assignment.classSectionId?.trim().orEmpty()
                    val label = listOfNotNull(assignment.className?.trim(), assignment.sectionName?.trim())
                        .filter { it.isNotBlank() }
                        .joinToString("-")
                    if (id.isBlank() || label.isBlank()) null else label to id
                }.distinctBy { it.second }.toMap()
            } else {
                ClassSectionRepository.loadSchoolClassSectionLookup(schoolId)
                    .getOrElse { throwable ->
                        scLog("[DATA_RETRIEVE_SC] HomeworkViewModel class lookup failed school=$schoolId", throwable)
                        emptyList()
                    }.mapNotNull { item ->
                        val id = item.id?.trim().orEmpty()
                        val label = listOfNotNull(item.className?.trim(), item.sectionName?.trim())
                            .filter { it.isNotBlank() }
                            .joinToString("-")
                        if (id.isBlank() || label.isBlank()) null else label to id
                    }.toMap()
            }
            val subjectIds = SubjectRepository.loadSchoolSubjectsPage(
                schoolId = schoolId,
                isActive = true,
                pageIndex = 0,
                pageSize = 100
            ).getOrNull().orEmpty().mapNotNull { subject ->
                val id = subject.id?.trim().orEmpty()
                val name = subject.name?.trim().orEmpty()
                if (id.isBlank() || name.isBlank()) null else name to id
            }.toMap()
            val subjectNameById = subjectIds.entries.associate { (name, id) -> id to name }
            val subjectOptionsByClassId = buildMap {
                if (isTeacher) {
                    teacherAssignmentsLookup.await()?.teachingAssignments.orEmpty()
                        .filter { it.isActive != false }
                        .groupBy { it.classSectionId?.trim().orEmpty() }
                        .filterKeys { it.isNotBlank() && it in classIds.values }
                        .forEach { (classSectionId, assignments) ->
                            put(
                                classSectionId,
                                assignments.mapNotNull { assignment ->
                                    val subjectId = assignment.subjectId?.trim().orEmpty()
                                    val subjectName = assignment.subjectName?.trim().orEmpty()
                                        .ifBlank { subjectNameById[subjectId].orEmpty() }
                                    if (subjectId.isBlank() || subjectName.isBlank()) null else subjectName to subjectId
                                }.toMap()
                            )
                        }
                } else {
                    // School admins use the same school-wide staff-with-assignments
                    // source as teachers. Build the class -> subject map from that
                    // response instead of issuing one subject-link request per class.
                    schoolTeacherAssignmentsLookup.await()
                        .flatMap { staff -> staff.teachingAssignments.orEmpty() }
                        .filter { it.isActive != false }
                        .groupBy { it.classSectionId?.trim().orEmpty() }
                        .filterKeys { it.isNotBlank() && it in classIds.values }
                        .forEach { (classSectionId, assignments) ->
                            put(
                                classSectionId,
                                assignments.mapNotNull { assignment ->
                                    val subjectId = assignment.subjectId?.trim().orEmpty()
                                    val subjectName = assignment.subjectName?.trim().orEmpty()
                                        .ifBlank { subjectNameById[subjectId].orEmpty() }
                                    if (subjectId.isBlank() || subjectName.isBlank()) null else subjectName to subjectId
                                }.toMap()
                            )
                        }
                }
            }
            val availableSubjectIds = if (isTeacher) {
                subjectOptionsByClassId.values.flatMap { it.values }.toSet()
            } else {
                emptySet()
            }
            _uiState.update {
                it.copy(
                    classOptionIds = classIds,
                    subjectOptionIds = if (isTeacher) {
                        subjectIds.filterValues { subjectId -> subjectId in availableSubjectIds }
                    } else {
                        subjectIds
                    },
                    subjectOptionIdsByClassId = subjectOptionsByClassId
                )
            }
        }
    }

    private fun loadPage(forceRemote: Boolean = false, debounce: Boolean = false) {
        loadJob?.cancel()
        loadJob = scope.launch {
            if (debounce) delay(300)
            val state = _uiState.value
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            val filters = state.filters
            val statsDeferred = async { HomeworkListRepository.loadStatistics() }
            val pageDeferred = async {
                HomeworkListRepository.loadPage(
                    search = state.searchQuery,
                    classSectionId = filters.classId,
                    subjectId = filters.subjectId,
                    status = filters.status.toApiStatus(),
                    pageIndex = state.pageIndex,
                    pageSize = state.pageSize,
                    sortBy = state.sortField.toApiSortField(),
                    order = if (state.sortAscending) "asc" else "desc",
                    forceRemote = forceRemote,
                    refreshScope = scope
                )
            }
            val statsResult = statsDeferred.await()
            statsResult.onFailure { throwable ->
                scLog("[DATA_RETRIEVE_SC] HomeworkViewModel statistics load failure", throwable)
            }
            pageDeferred.await().fold(
                onSuccess = { response ->
                    val homework = response.items
                    val rows = homework.map { it.toUi(state) }
                    val counters = response.counters
                    val statsCounters = statsResult.getOrNull()?.counters
                    val subjectsFromHomework = homework.mapNotNull { item ->
                        val classSectionId = item.classSectionId?.trim().orEmpty()
                        val subjectId = item.subjectId?.trim().orEmpty()
                        val subjectName = item.subjectName?.trim().orEmpty()
                        if (classSectionId.isBlank() || subjectId.isBlank() || subjectName.isBlank()) null
                        else classSectionId to (subjectName to subjectId)
                    }.groupBy({ it.first }, { it.second }).mapValues { (_, subjects) -> subjects.toMap() }
                    val filteredTotal = response.count
                    val validPageIndex = com.schoolconnect.app.feature.common.pageWindow(
                        state.pageIndex,
                        state.pageSize,
                        filteredTotal
                    ).pageIndex
                    if (validPageIndex != state.pageIndex) {
                        _uiState.update { it.copy(totalCount = filteredTotal, pageIndex = validPageIndex, isLoading = false) }
                        loadPage(forceRemote = forceRemote)
                        return@fold
                    }
                    _uiState.update { current ->
                        current.copy(
                            rows = rows,
                            totalCount = filteredTotal,
                            totalHomework = statsCounters?.total ?: filteredTotal,
                            assignedByMe = statsCounters?.assignedByMe ?: 0,
                            allOpen = statsCounters?.open ?: counters.open ?: 0,
                            closed = statsCounters?.closed ?: counters.closed ?: 0,
                            overdue = statsCounters?.overdue ?: counters.overdue ?: 0,
                            classOptionIds = current.classOptionIds + homework.mapNotNull { item ->
                                val row = item.toUi(current)
                                val id = item.classSectionId.orEmpty()
                                if (id.isBlank() || row.className == "—") null else row.className to id
                            },
                            subjectOptionIds = current.subjectOptionIds + homework.mapNotNull { item ->
                                val row = item.toUi(current)
                                val id = item.subjectId.orEmpty()
                                if (id.isBlank() || row.subject == "—") null else row.subject to id
                            },
                            subjectOptionIdsByClassId = current.subjectOptionIdsByClassId +
                                subjectsFromHomework.mapValues { (classSectionId, subjects) ->
                                    current.subjectOptionIdsByClassId[classSectionId].orEmpty() + subjects
                                },
                            statusOptions = (current.statusOptions + homework.mapNotNull { item ->
                                item.status?.trim()?.takeIf { it.isNotBlank() }
                            }).distinct().sorted(),
                            isLoading = false,
                            errorMessage = ""
                        )
                    }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] HomeworkViewModel loadPage failure forceRemote=$forceRemote", throwable)
                    _uiState.update { it.copy(rows = emptyList(), totalCount = 0, isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load homework." }) }
                }
            )
        }
    }

    fun clear() {
        loadJob?.cancel()
        lookupJob?.cancel()
        saveJob?.cancel()
        attachmentMetadataJob?.cancel()
        viewJob?.cancel()
        actionJob?.cancel()
        scope.cancel()
    }

    private fun parseDisplayDate(value: String): String? {
        val parts = value.trim().split(" ").filter { it.isNotBlank() }
        if (parts.size != 3) return null
        val month = mapOf("JAN" to "01", "FEB" to "02", "MAR" to "03", "APR" to "04", "MAY" to "05", "JUN" to "06", "JUL" to "07", "AUG" to "08", "SEP" to "09", "OCT" to "10", "NOV" to "11", "DEC" to "12")[parts[1].uppercase()] ?: return null
        val day = parts[0].toIntOrNull() ?: return null
        val year = parts[2].toIntOrNull() ?: return null
        if (day !in 1..31 || year !in 1900..2200) return null
        return "${year.toString().padStart(4, '0')}-$month-${day.toString().padStart(2, '0')}"
    }
}
private fun String?.isImageFileName(): Boolean = when (this.orEmpty().substringAfterLast('.', "").lowercase()) {
    "jpg", "jpeg", "png", "gif", "webp", "bmp", "avif" -> true
    else -> false
}
private fun String?.toAttachmentType(filename: String?): String {
    val extension = filename.orEmpty().substringAfterLast('.', "").takeIf { it.isNotBlank() }
    return extension?.uppercase() ?: this?.substringAfter('/', "")?.uppercase()?.takeIf { it.isNotBlank() } ?: "FILE"
}

private fun formatHomeworkFileSize(bytes: Long?): String = when {
    bytes == null || bytes <= 0L -> "Size unavailable"
    bytes < 1024L -> "$bytes B"
    bytes < 1024L * 1024L -> "${bytes / 1024L} KB"
    else -> "${bytes / (1024L * 1024L)} MB"
}

internal fun String?.toApiStatus(): String? = this?.trim()?.takeIf {
    it.isNotBlank() && !it.equals("All", ignoreCase = true)
}

internal fun HomeworkSortField.toApiSortField(): String = when (this) {
    HomeworkSortField.ID -> "homework_id"
    HomeworkSortField.TITLE -> "title"
    HomeworkSortField.CLASS -> "class"
    HomeworkSortField.SUBJECT -> "subject"
    // Kept for the visible UI; backend must add this sort key to make it effective.
    HomeworkSortField.ASSIGNED_BY -> "assigned_by"
    HomeworkSortField.ASSIGNED_DATE -> "created_at"
    HomeworkSortField.DUE_DATE -> "due_date"
    HomeworkSortField.STATUS -> "status"
}

internal fun HomeworkResponse.toUi(state: HomeworkUiState): HomeworkRowUi {
    val classLabel = listOfNotNull(className, sectionName).joinToString("-").ifBlank {
        state.classOptionIds.entries.firstOrNull { it.value == classSectionId }?.key.orEmpty()
    }.ifBlank { "—" }
    val subjectLabel = subjectName.orEmpty().ifBlank {
        state.subjectOptionIds.entries.firstOrNull { it.value == subjectId }?.key.orEmpty()
    }.ifBlank { "—" }
    return HomeworkRowUi(
        backendId = id.orEmpty(),
        schoolId = schoolId.orEmpty(),
        classSectionId = classSectionId.orEmpty(),
        subjectId = subjectId.orEmpty(),
        homeworkId = homeworkId.orEmpty().ifBlank { id.orEmpty() }.ifBlank { "—" },
        title = title.orEmpty().ifBlank { "Untitled homework" },
        className = classLabel,
        subject = subjectLabel,
        assignedBy = assignedByTeacherName.orEmpty().ifBlank { "—" },
        assignedByTeacherId = assignedByTeacherId.orEmpty(),
        assignedDate = createdAt.toDisplayDate(),
        updatedDate = updatedAt.toDisplayDateTime(),
        dueDate = dueDate.toDisplayDate(),
        // Preserve the exact backend response key; do not infer a display status from due_date.
        status = status.orEmpty().trim().ifBlank { "—" },
        notifyParents = notifyParents,
        description = description.orEmpty(),
        attachmentFileIds = attachmentFileIds,
        attachmentFileUrls = attachmentFileUrls
    )
}

internal fun String?.toDisplayDate(): String {
    val value = this.orEmpty().trim()
    val parts = value.substringBefore('T').split('-')
    if (parts.size != 3) return value.ifBlank { "—" }
    val month = parts[1].toIntOrNull() ?: return value
    val monthLabel = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").getOrNull(month - 1) ?: return value
    return "${parts[2].padStart(2, '0')} $monthLabel ${parts[0]}"
}

private fun String?.toDisplayDateTime(): String {
    val raw = this.orEmpty()
    if (raw.isBlank()) return "—"
    val date = raw.toDisplayDate()
    val time = raw.substringAfter('T', "").take(5)
    return if (time.length == 5) "$date, $time" else date
}
